/**
 * SubscriptionSection Component
 * Manage subscription with subscribed and unsubscribed states
 */

import type { ApiError } from "@shared-types/api.types"
import type { AxiosError } from "axios"
import { Check } from "lucide-react"
import { useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { useTranslation } from "@/language"
import { transformApiError } from "@/lib/errorTransformer"
import {
  useActiveSubscriptionPlan,
  useCancelSubscription,
  useCreatePaymentUpdateSession,
  useCreateSubscriptionCheckout,
  useSubscription,
} from "../hooks/useSettings"
import type {
  ActiveSubscriptionPlanDetails,
  SubscriptionDetails,
} from "../services/settings.service"

const PLAN_FEATURES = (t: (key: string) => string) => [
  t("subscription.features.accessRituals"),
  t("subscription.features.unlimitedCommunity"),
  t("subscription.features.billingPortal"),
  t("subscription.features.cancelAnytime"),
]

export function SubscriptionSection() {
  const { t } = useTranslation("settings")

  const { data: subscriptionData, isLoading, error: loadError } = useSubscription()
  const { data: activePlan } = useActiveSubscriptionPlan()

  const createCheckout = useCreateSubscriptionCheckout()
  const cancelSubscription = useCancelSubscription()
  const createPaymentUpdate = useCreatePaymentUpdateSession()

  const subscription = subscriptionData?.subscription ?? null
  const hasSubscription = subscriptionData?.hasSubscription ?? false
  const isPending =
    createCheckout.isPending || cancelSubscription.isPending || createPaymentUpdate.isPending
  const errorMessage = getSettingsErrorMessage(
    loadError ?? createCheckout.error ?? cancelSubscription.error ?? createPaymentUpdate.error,
    t("subscription.errorGeneric"),
  )

  const handleSubscribe = () => {
    const baseUrl = window.location.origin
    createCheckout.mutate({
      successUrl: `${baseUrl}/settings?success=true`,
      cancelUrl: `${baseUrl}/settings?canceled=true`,
    })
  }

  const handleCancelSubscription = () => {
    cancelSubscription.mutate()
  }

  const handleUpdatePayment = () => {
    const baseUrl = window.location.origin
    createPaymentUpdate.mutate(`${baseUrl}/settings?section=subscription`)
  }

  // Redirect on successful checkout/payment creation - useEffect prevents render-time redirect
  useEffect(() => {
    if (createCheckout.data?.checkoutUrl) {
      window.location.href = createCheckout.data.checkoutUrl
    } else if (createPaymentUpdate.data?.url) {
      window.open(createPaymentUpdate.data.url, "_blank")
    }
  }, [createCheckout.data?.checkoutUrl, createPaymentUpdate.data?.url])

  if (isLoading) {
    return (
      <div className="flex flex-col gap-8">
        <div>
          <h1 className="text-lg md:text-xl font-semibold text-primary leading-5">
            {t("subscription.title")}
          </h1>
          <p className="text-sm text-primary mt-2.5 md:mt-1.25">{t("subscription.description")}</p>
        </div>
        <Separator />
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Section Header */}
      <div>
        <h1 className="text-lg md:text-xl font-semibold text-primary leading-5 uppercase">
          {t("subscription.title")}
        </h1>
        <p className="text-sm text-primary mt-2.5 md:mt-1.25">{t("subscription.description")}</p>
      </div>

      {errorMessage && (
        <div className="bg-red/10 text-red px-4 py-2 rounded-lg text-sm">{errorMessage}</div>
      )}

      {hasSubscription ? (
        <SubscribedView
          subscription={subscription}
          onCancel={handleCancelSubscription}
          onUpdatePayment={handleUpdatePayment}
          isProcessing={isPending}
        />
      ) : (
        <UnsubscribedView
          activePlan={activePlan}
          onSubscribe={handleSubscribe}
          isProcessing={isPending}
        />
      )}
    </div>
  )
}

function getSettingsErrorMessage(error: unknown, fallbackMessage: string): string | null {
  if (!error) {
    return null
  }

  const axiosError = error as AxiosError
  if (axiosError?.isAxiosError) {
    return transformApiError(axiosError as AxiosError<ApiError>) || fallbackMessage
  }

  if (error instanceof Error && error.message) {
    return error.message
  }

  return fallbackMessage
}

function UnsubscribedView({
  activePlan,
  onSubscribe,
  isProcessing,
}: {
  activePlan?: ActiveSubscriptionPlanDetails
  onSubscribe: () => void
  isProcessing: boolean
}) {
  const { t } = useTranslation("settings")
  const features = PLAN_FEATURES(t)
  const planTitle = activePlan?.name || t("subscription.plan")
  const price = activePlan
    ? formatCurrencyAmount(activePlan.priceInCents, activePlan.currency)
    : "-"
  const pricePeriod = formatPlanInterval(activePlan?.interval, activePlan?.intervalCount, t)

  return (
    <div className="flex flex-col gap-6 max-w-md">
      {/* Plan Card */}
      <div className="rounded-xl border border-gray p-6 flex flex-col gap-4">
        <div>
          <h2 className="text-lg font-semibold text-charcoal">{planTitle}</h2>
          <div className="flex items-baseline gap-1 mt-1">
            <span className="text-3xl font-bold text-charcoal">{price}</span>
            <span className="text-sm text-stone">{pricePeriod}</span>
          </div>
          {activePlan?.description ? (
            <p className="text-sm text-stone mt-2">{activePlan.description}</p>
          ) : null}
        </div>

        <Separator />

        <ul className="flex flex-col gap-3">
          {features.map(feature => (
            <li key={feature} className="flex items-center gap-3">
              <div className="size-5 rounded-full bg-green/10 flex items-center justify-center shrink-0">
                <Check className="size-3 text-green" />
              </div>
              <span className="text-sm text-charcoal">{feature}</span>
            </li>
          ))}
        </ul>

        <div className="pt-2">
          <Button
            variant="primary"
            size="md"
            fullWidth
            onClick={onSubscribe}
            disabled={isProcessing}
          >
            {isProcessing ? t("subscription.processing") : t("subscription.subscribe")}
          </Button>
        </div>
      </div>
    </div>
  )
}

function SubscribedView({
  subscription,
  onCancel,
  onUpdatePayment,
  isProcessing,
}: {
  subscription: SubscriptionDetails | null
  onCancel: () => void
  onUpdatePayment: () => void
  isProcessing: boolean
}) {
  const { t } = useTranslation("settings")

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-"
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    })
  }

  const billingCycleLabel = formatPlanInterval(
    subscription?.billingInterval,
    subscription?.billingIntervalCount,
    t,
  )
  const amountLabel =
    subscription?.planPrice && subscription?.currency
      ? `${formatCurrencyAmount(subscription.planPrice, subscription.currency)} / ${billingCycleLabel}`
      : "-"

  return (
    <div className="flex flex-col gap-6 max-w-md">
      {/* Active Plan */}
      <div className="rounded-xl border border-gray p-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-charcoal">
            {subscription?.planName || t("subscription.plan")}
          </h2>
          <span
            className={`text-xs font-semibold uppercase px-2.5 py-1 rounded-full ${
              subscription?.cancelAtPeriodEnd
                ? "bg-brand-terracotta text-[#000]"
                : subscription?.status === "TRIALING"
                  ? "bg-purple/10 text-purple"
                  : "bg-green/10 text-green"
            }`}
          >
            {subscription?.cancelAtPeriodEnd
              ? "CANCELLATION PENDING"
              : subscription?.status === "TRIALING"
                ? "TRIALING"
                : "ACTIVE"}
          </span>
        </div>

        <Separator />

        {/* Billing Details */}
        <div className="flex flex-col gap-3">
          <div className="flex justify-between">
            <span className="text-sm text-stone">{t("subscription.billingCycle")}</span>
            <span className="text-sm font-medium text-charcoal">{billingCycleLabel}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-stone">
              {subscription?.cancelAtPeriodEnd
                ? t("subscription.accessEnds")
                : t("subscription.nextBilling")}
            </span>
            <span className="text-sm font-medium text-charcoal">
              {formatDate(subscription?.currentPeriodEnd || null)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-stone">{t("subscription.amount")}</span>
            <span className="text-sm font-medium text-charcoal">{amountLabel}</span>
          </div>
        </div>

        <Separator />

        {/* Billing portal is the source of truth for payment method details. */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-charcoal">{t("subscription.paymentMethodManaged")}</span>
            <span className="text-xs text-stone">{t("subscription.paymentMethodManagedHint")}</span>
          </div>
          <button
            type="button"
            onClick={onUpdatePayment}
            disabled={isProcessing}
            className="text-sm font-medium text-stone hover:text-charcoal transition-colors cursor-pointer disabled:opacity-50 border border-primary rounded-md px-3 py-1.5"
          >
            {isProcessing ? t("subscription.processing") : t("subscription.manageBilling")}
          </button>
        </div>
      </div>

      {/* Cancel Subscription */}
      {!subscription?.cancelAtPeriodEnd && (
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing || subscription?.cancelAtPeriodEnd}
          className="text-sm font-medium text-brand-terracotta-bright cursor-pointer self-start disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {subscription?.cancelAtPeriodEnd
            ? t("subscription.cancellationPending")
            : t("subscription.cancelSubscription")}
        </button>
      )}
    </div>
  )
}

function formatCurrencyAmount(amountInCents: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amountInCents / 100)
}

function formatPlanInterval(
  interval: string | null | undefined,
  intervalCount: number | null | undefined,
  t: (key: string) => string,
) {
  if (!interval || interval === "month") {
    return !intervalCount || intervalCount === 1
      ? t("subscription.monthly")
      : `${intervalCount} months`
  }

  if (!intervalCount || intervalCount === 1) {
    return interval
  }

  return `${intervalCount} ${interval}s`
}
